import {connect} from 'react-redux';

import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {getIsUserGlobalAdmin} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {CellsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {isDemoInbox} from '../../../../helpers/modelHelpers';
import {StartGuideTaskTypesEnum} from '../../../../models/api/preferencesModel';
import {AppState} from '../../../../store/appState';
import {
  getPinnedSandboxWorkspaceInboxes,
  getUserOwnedInboxes,
} from '../../../../store/data/inboxes/inboxesSelectors';
import {getLenses} from '../../../../store/data/lenses/lensesSelectors';
import {updateStartGuideTasksAction} from '../../../../store/data/preferences/preferencesActions';
import {getCompletedStartGuideTasks} from '../../../../store/data/preferences/preferencesSelectors';
import {getHasTeammates} from '../../../../store/data/teammates/teammatesSelectors';
import {getSandboxTeamUserIsPartOf} from '../../../../store/data/teams/teamsSelectors';
import {
  CellsPanelPageParametersSelector,
  CellsPanelPageSelector,
  makeGetCellsPanelCurrentPage,
} from '../../../../store/panels/cells/cellsPanelSelectors';
import {makeGetIsReviewingAiTags} from '../../../../store/panels/inboxes/inboxesPanelSelectors';
import {
  hasLensInboxParameters,
  hasSearchInInboxesParameters,
  InboxesParametersTypesEnum,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  findSelectedRefinementFromProps,
  inboxParametersHasAssigneeLensCriteria,
  inboxParametersHasUnassignedFilter,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {getAvailableSetupGuide, SetupGuideEnum} from '../../../../store/stateless/onboardingSelectors';
import {searchByParametersAction} from '../../../../store/stateless/searchActions';
import {rulesInSetupGuideAdminOnboardingTaskDefinitions} from '../../../onboarding/tasks/adminOnboardingTasks';
import {fluOnboardingTaskDefinitions} from '../../../onboarding/tasks/fluOnboardingTasks';
import {
  getAdminOnboardingTasks,
  getFluOnboardingTasks,
} from '../../../onboarding/tasks/onboardingTaskSelectors';
import {CellsEmptySetupActionsVariantsEnum} from '../../cells/content/cellsEmpty/cellsEmptyCommon';
import {EmptyInboxes} from './emptyInboxes';

/*
 * Props.
 */

interface EmptyInboxesContainerProps {
  panelId: CellsPanelId;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: AppState, props: EmptyInboxesContainerProps) {
  return props.panelId;
}

function makeGetCellsPanelPageParameters(getPage: CellsPanelPageSelector<EmptyInboxesContainerProps>) {
  return createSelector([getPage], (page) => page.parameters);
}

function makeGetCellsPanelPageCellsContext(getPage: CellsPanelPageSelector<EmptyInboxesContainerProps>) {
  return createSelector([getPage], (page) => page.cellsContext);
}

const getHasCompletedAdminSetupGuide = (state: AppState) => {
  const tasks = getAdminOnboardingTasks(state, {
    taskDefinitions: rulesInSetupGuideAdminOnboardingTaskDefinitions,
    completedCourses: undefined,
    // Don't check if courses were completed, because fetching this value requires a network call,
    // and is therefor too "expensive" for the empty state. undefined assumes the course has been
    // completed already.
    shouldAssumeCoursesAreComplete: true,
  });

  return tasks.setup.progress.hasCompletedAllTasks;
};

const getHasCompletedFrontlineUserSetupGuide = (state: AppState) => {
  const tasks = getFluOnboardingTasks(state, {
    taskDefinitions: fluOnboardingTaskDefinitions,
    panelId: undefined,
  });

  return tasks.start.progress.hasCompletedAllTasks;
};

const getHasCompletedSetupGuide = (state: AppState) => {
  const availableSetupGuide = getAvailableSetupGuide(state);

  switch (availableSetupGuide) {
    case SetupGuideEnum.ADMIN:
      return getHasCompletedAdminSetupGuide(state);
    case SetupGuideEnum.FRONT_LINE_USER:
      return getHasCompletedFrontlineUserSetupGuide(state);
    default:
      return false;
  }
};

export function makeGetSetupActionsVariant<P>(getParameters: CellsPanelPageParametersSelector<P>) {
  return createSelector(
    [
      getUserOwnedInboxes,
      getHasTeammates,
      getIsUserGlobalAdmin,
      getCompletedStartGuideTasks,
      getParameters,
      getSandboxTeamUserIsPartOf,
      getPinnedSandboxWorkspaceInboxes,
      getHasCompletedSetupGuide,
      getAvailableSetupGuide,
    ],
    (
      userOwnedInboxes,
      hasTeammates,
      isUserGlobalAdmin,
      completedStartGuideTasks,
      parameters,
      sandboxTeam,
      sandboxInboxes,
      hasCompletedSetupGuide,
      setupGuide,
    ) => {
      const {type} = parameters;
      const shouldAddIndividualInbox = userOwnedInboxes.every((inbox) => isDemoInbox(inbox));

      // If we are in the sandbox.
      const isSandboxParameters =
        (parameters.type === InboxesParametersTypesEnum.TEAM && parameters.teamId === sandboxTeam?.id) ||
        (parameters.type === InboxesParametersTypesEnum.INBOX &&
          sandboxInboxes.some((inbox) => inbox.id === parameters.inboxId));

      if (isSandboxParameters && !hasSearchInInboxesParameters(parameters)) {
        return CellsEmptySetupActionsVariantsEnum.SANDBOX;
      }

      // If we are in a team inbox or workspace and should complete the setup guide.
      if (
        (type === InboxesParametersTypesEnum.INBOX ||
          type === InboxesParametersTypesEnum.TEAM ||
          type === InboxesParametersTypesEnum.USER ||
          type === InboxesParametersTypesEnum.TEAMMATE_INBOX ||
          type === InboxesParametersTypesEnum.TEAMMATE_VIEW ||
          type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) &&
        setupGuide &&
        !hasCompletedSetupGuide &&
        !hasSearchInInboxesParameters(parameters)
      ) {
        switch (setupGuide) {
          case SetupGuideEnum.ADMIN:
            return CellsEmptySetupActionsVariantsEnum.ADMIN_SETUP_GUIDE;
          case SetupGuideEnum.FRONT_LINE_USER:
            return CellsEmptySetupActionsVariantsEnum.FRONT_LINE_USER_SETUP_GUIDE;
          default:
            throw new UnreachableError(setupGuide);
        }
      }

      // If we are in the teammate workspace, have no user owned inboxes, and
      // have not dismissed the TEAMMATE setup actions, return TEAMMATE variant.
      if (
        (type === InboxesParametersTypesEnum.USER ||
          type === InboxesParametersTypesEnum.TEAMMATE_INBOX ||
          type === InboxesParametersTypesEnum.TEAMMATE_VIEW ||
          type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) &&
        shouldAddIndividualInbox &&
        !hasSearchInInboxesParameters(parameters) &&
        !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATE_DISMISS)
      ) {
        return CellsEmptySetupActionsVariantsEnum.TEAMMATE;
      }

      // If we are in the teammates workspace, have no teammates, are a global
      // admin, and have not dismissed the TEAMMATES setup actions, return TEAMMATES variant.
      if (
        type === InboxesParametersTypesEnum.GLOBAL &&
        !hasTeammates &&
        isUserGlobalAdmin &&
        !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATES_DISMISS)
      ) {
        return CellsEmptySetupActionsVariantsEnum.TEAMMATES;
      }

      return null;
    },
  );
}

const makeGetHasRefinedResults = (getPage: CellsPanelPageSelector<EmptyInboxesContainerProps>) =>
  createSelector([getPage], (page) => {
    const {parameters} = page;
    return findSelectedRefinementFromProps(parameters, page) !== undefined;
  });

const makeGetHasAssigneeLensCriteria = (getPage: CellsPanelPageSelector<EmptyInboxesContainerProps>) =>
  createSelector([getPage, getLenses], (page, lenses) => {
    const {parameters} = page;
    return inboxParametersHasAssigneeLensCriteria(parameters, lenses);
  });

const makeGetHasUnassignedFilter = (getPage: CellsPanelPageSelector<EmptyInboxesContainerProps>) =>
  createSelector([getPage], (page) => {
    const {parameters} = page;
    return inboxParametersHasUnassignedFilter(parameters);
  });

function makeGetInboxesParametersLens<P>(getParameters: CellsPanelPageParametersSelector<P>) {
  return createSelector([getParameters, getLenses], (parameters, lenses) => {
    if (!hasLensInboxParameters(parameters)) {
      return undefined;
    }
    return lenses[parameters.lensId];
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<EmptyInboxesContainerProps>()(() => {
  const getCellsPanelCurrentPage = makeGetCellsPanelCurrentPage(getCellsPanelId);
  const getCellsPanelPageParameters = makeGetCellsPanelPageParameters(getCellsPanelCurrentPage);
  const getHasRefinedResults = makeGetHasRefinedResults(getCellsPanelCurrentPage);
  const getHasAssigneeLensCriteria = makeGetHasAssigneeLensCriteria(getCellsPanelCurrentPage);
  const getHasUnassignedFilter = makeGetHasUnassignedFilter(getCellsPanelCurrentPage);

  return {
    ctaVariant: makeGetSetupActionsVariant(getCellsPanelPageParameters),
    selectedLens: makeGetInboxesParametersLens(getCellsPanelPageParameters),
    hasRefinedResults: getHasRefinedResults,
    hasAssigneeLensCriteria: getHasAssigneeLensCriteria,
    hasUnassignedFilter: getHasUnassignedFilter,
    inboxesParameters: getCellsPanelPageParameters,
    cellsContext: makeGetCellsPanelPageCellsContext(getCellsPanelCurrentPage),
    isReviewingAiTags: makeGetIsReviewingAiTags(getCellsPanelPageParameters),
  };
});

const mapDispatchToProps = {
  navigateToLinkAction,
  navigateToSearch: searchByParametersAction,
  updateStartGuideTasksAction,
};

export const EmptyInboxesContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const navigate = (destination: string) => () => dispatchProps.navigateToLinkAction(destination);
    const dismissCellsEmptySetupActionsVariant = (task: StartGuideTaskTypesEnum) => () =>
      dispatchProps.updateStartGuideTasksAction(task);

    return {
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      navigate,
      dismissCellsEmptySetupActionsVariant,
    };
  },
)(EmptyInboxes);
